import React, { useEffect, useState, useCallback } from "react";
import {
  Stack,
  Modal,
  Typography,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import ReactJson from "react-json-view";
import { parseConfigField, preprocessJson } from "../helper/explorationConfigHelpers";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  overflow: "auto",
  maxHeight: "80vh",
};

/** Labels and one-line descriptions for each configuration blob. */
const CONFIG_CARD_META = [
  {
    configKey: "exploration_config",
    title: "Optimization",
    description:
      "Optimization and ranking options for this run, including objectives and how results are scored.",
  },
  {
    configKey: "laser_config",
    title: "Laser",
    description:
      "The design space cloned from the selected laser at creation: parameter grids, limits and defaults used for marking.",
  },
  {
    configKey: "plate_config",
    title: "Plate design",
    description:
      "Plate layout and patch geometry: rows, columns, spacing, and related design constraints.",
  },
  {
    configKey: "width_measurement_config",
    title: "Width measurement",
    description:
      "Microscope line detection and width measurement: thresholds, ROI, and calibration usage.",
  },
  {
    configKey: "color_measurement_config",
    title: "Color measurement",
    description:
      "Built from the selected camera calibration (matrix, reduction factors, visualization gain).",
  },
];

/**
 * Read-only view of an exploration's configurations. ALL configs are frozen
 * once the exploration is created (the backend refuses late writes with
 * EXPLORATION_CONFIGS_FROZEN): an exploration is an immutable experiment
 * record — different settings mean a new exploration, optionally warm-started
 * from this one's sample points.
 */
export default function ExplorationConfigsPanel({ explorationId }) {
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [openModal, setOpenModal] = useState(false);
  const [jsonToShow, setJsonToShow] = useState({});
  const [configTitle, setConfigTitle] = useState("");

  const [configs, setConfigs] = useState({
    exploration_config: {},
    laser_config: {},
    plate_config: {},
    width_measurement_config: {},
    color_measurement_config: {},
  });

  const loadExploration = useCallback(async () => {
    if (!explorationId) {
      setLoadError("Missing exploration id.");
      setLoading(false);
      return;
    }

    setLoadError(null);
    setLoading(true);

    try {
      const expRes = await axios.get(`api/color/explorer/exploration/${explorationId}/`);
      const exp = expRes.data;

      const next = {
        exploration_config: preprocessJson(parseConfigField(exp.exploration_config) || {}),
        laser_config: preprocessJson(parseConfigField(exp.laser_config) || {}),
        plate_config: preprocessJson(parseConfigField(exp.plate_design_config) || {}),
        width_measurement_config: preprocessJson(parseConfigField(exp.width_measurement_config) || {}),
        color_measurement_config: {},
      };

      const validUuidLike = (id) =>
        id && id !== "null" && id !== "undefined" && String(id).length > 0;

      if (validUuidLike(exp.colorimetric_calibration)) {
        try {
          const cRes = await axios.get(
            `api/color/explorer/color/calibration/?id=${encodeURIComponent(
              exp.colorimetric_calibration
            )}`
          );
          if (cRes.status === 200 && cRes.data?.length > 0) {
            const cfg = parseConfigField(cRes.data[0].colorimetric_calibration_config);
            if (cfg && typeof cfg === "object") {
              next.color_measurement_config = preprocessJson(cfg);
            }
          }
        } catch (e) {
          console.warn("Color calibration config load:", e);
        }
      }

      setConfigs(next);
    } catch (err) {
      console.error(err);
      setLoadError(
        err.response?.status === 404
          ? "Exploration not found or you do not have access."
          : "Could not load the exploration configurations."
      );
    } finally {
      setLoading(false);
    }
  }, [explorationId]);

  useEffect(() => {
    loadExploration();
  }, [loadExploration]);

  const handleOpenModal = (configKey, title) => {
    setJsonToShow(configs[configKey] || {});
    setConfigTitle(title);
    setOpenModal(true);
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          Loading configurations…
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {loadError ? (
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        All configurations are fixed when the exploration is created and cannot be changed
        afterwards — an exploration is an immutable experiment record. To run with different
        settings, start a new exploration (optionally warm-started from this one's sample
        points).
      </Alert>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          width: "100%",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {CONFIG_CARD_META.map(({ configKey, title, description }) => (
          <Card
            key={configKey}
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: "#e7ebf3",
              background: "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 2, flex: 1, lineHeight: 1.45 }}
              >
                {description}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600 }}
                onClick={() => handleOpenModal(configKey, title)}
              >
                View JSON
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={modalStyle}>
          <Typography id="view-config-modal-title" variant="h6" component="h2" sx={{ mb: 1 }}>
            {configTitle} — read-only
          </Typography>
          <ReactJson
            src={preprocessJson(jsonToShow || {})}
            enableClipboard
            displayDataTypes={false}
            collapsed={2}
          />
          <Button variant="contained" onClick={() => setOpenModal(false)} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Modal>
    </Stack>
  );
}
